import { useState } from "react";
import { useT } from "@/i18n/I18nProvider";
import { submitContact, type ContactPayload } from "@/lib/api";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

type FieldErrors = Partial<Record<keyof ContactPayload, string>>;

interface FormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  interest: string;
  message: string;
  consent: boolean;
  /** Honeypot — invisible to humans, filled only by spam bots. */
  website: string;
}

const INITIAL: FormState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  interest: "",
  message: "",
  consent: false,
  website: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm() {
  const { t, locale } = useT();
  const [values, setValues] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [reference, setReference] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const interests: Array<{ id: string; label: string }> = [
    { id: "trade", label: t.contact.interests.trade },
    { id: "sourcing", label: t.contact.interests.sourcing },
    { id: "supply", label: t.contact.interests.supply },
    { id: "consulting", label: t.contact.interests.consulting },
    { id: "grains", label: t.contact.interests.grains },
    { id: "food", label: t.contact.interests.food },
    { id: "petro", label: t.contact.interests.petro },
    { id: "other", label: t.contact.interests.other },
  ];

  function validate(v: FormState): FieldErrors {
    const e: FieldErrors = {};
    if (v.name.trim().length < 2) e.name = t.contact.errors.required;
    if (v.company.trim().length < 2) e.company = t.contact.errors.required;
    if (!EMAIL_RE.test(v.email.trim())) e.email = t.contact.errors.email;
    if (v.message.trim().length < 10) e.message = t.contact.errors.minLen;
    if (!v.consent) e.consent = t.contact.errors.consent;
    return e;
  }

  async function onSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const e = validate(values);
    setErrors(e);
    if (Object.keys(e).length) return;

    setStatus("sending");
    setErrorMsg(null);
    try {
      const res = await submitContact({
        name: values.name.trim(),
        company: values.company.trim(),
        email: values.email.trim(),
        phone: values.phone.trim() || undefined,
        country: values.country.trim() || undefined,
        interest: values.interest || undefined,
        message: values.message.trim(),
        consent: values.consent,
        locale,
        website: values.website,
      });
      setReference(res.reference);
      setStatus("success");
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        t.contact.errorGeneric;
      setErrorMsg(String(detail));
      setStatus("error");
    }
  }

  function bind<K extends keyof FormState>(field: K) {
    return {
      value: values[field] as never,
      onChange: (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const target = evt.target as HTMLInputElement;
        const val = target.type === "checkbox" ? (target.checked as unknown as FormState[K]) : (target.value as unknown as FormState[K]);
        setValues((s) => ({ ...s, [field]: val }));
      },
    };
  }

  if (status === "success" && reference) {
    return (
      <div
        className="border border-ink bg-bone p-8 md:p-12"
        role="status"
        aria-live="polite"
        data-testid="contact-success"
      >
        <div className="flex items-start gap-4">
          <CheckCircle2 className="text-clay flex-shrink-0 mt-1" size={28} aria-hidden />
          <div>
            <p className="eyebrow mb-3">{t.contact.formEyebrow}</p>
            <h3 className="font-display font-light text-3xl md:text-4xl text-ink mb-4">
              {t.contact.success}
            </h3>
            <p className="text-slate2 leading-relaxed">
              {t.contact.successBody}{" "}
              <span className="font-mono text-ink" data-testid="contact-success-reference">
                {reference}
              </span>
              .
            </p>
            <p className="text-slate2 mt-2 text-sm">{t.contact.successHint}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="flex flex-col gap-10"
      data-testid="contact-form"
      aria-labelledby="contact-form-title"
    >
      {/* Honeypot — invisible to humans, filled only by spam bots.
          Reject in the Worker if non-empty. Kept out of the tab order and
          the accessibility tree; no visual footprint. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => setValues((s) => ({ ...s, website: e.target.value }))}
          data-testid="contact-honeypot"
        />
      </div>
      <div>
        <p className="eyebrow mb-3">{t.contact.formEyebrow}</p>
        <h2 id="contact-form-title" className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight text-ink">
          {t.contact.formTitle}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
        <TextField label={t.contact.fields.name} name="name" required error={errors.name} testId="contact-name" {...bind("name")} />
        <TextField label={t.contact.fields.company} name="company" required error={errors.company} testId="contact-company" {...bind("company")} />
        <TextField label={t.contact.fields.email} name="email" type="email" required error={errors.email} testId="contact-form-email" {...bind("email")} />
        <TextField label={t.contact.fields.phone} name="phone" type="tel" testId="contact-form-phone" {...bind("phone")} />
        <TextField label={t.contact.fields.country} name="country" testId="contact-country" {...bind("country")} />
        <SelectField
          label={t.contact.fields.interest}
          name="interest"
          testId="contact-interest"
          options={interests}
          {...bind("interest")}
        />
        <div className="md:col-span-2">
          <TextAreaField
            label={t.contact.fields.message}
            name="message"
            required
            rows={6}
            error={errors.message}
            testId="contact-message"
            {...bind("message")}
          />
        </div>
        <div className="md:col-span-2 flex items-start gap-3 pt-2">
          <input
            id="consent"
            name="consent"
            type="checkbox"
            checked={values.consent}
            onChange={(e) => setValues((s) => ({ ...s, consent: e.target.checked }))}
            className="mt-1 h-4 w-4 accent-clay border-slate2"
            data-testid="contact-consent"
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? "consent-error" : undefined}
          />
          <label htmlFor="consent" className="text-sm text-slate2 leading-relaxed">
            {t.contact.fields.consent}
          </label>
        </div>
        {errors.consent && (
          <p id="consent-error" className="md:col-span-2 -mt-4 text-sm text-clay-600 flex items-center gap-2">
            <AlertCircle size={14} aria-hidden /> {errors.consent}
          </p>
        )}
      </div>

      {status === "error" && (
        <div
          className="border border-clay-600 bg-clay-600/5 px-6 py-4 flex items-start gap-3"
          role="alert"
          data-testid="contact-error"
        >
          <AlertCircle size={18} className="text-clay-600 mt-0.5" aria-hidden />
          <p className="text-clay-700 text-sm">{errorMsg}</p>
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={status === "sending"}
          className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
          data-testid="contact-submit"
        >
          {status === "sending" ? (
            <>
              <Loader2 size={16} className="animate-spin" aria-hidden />
              {t.contact.sending}
            </>
          ) : (
            <>{t.contact.submit}</>
          )}
        </button>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  name: string;
  required?: boolean;
  error?: string;
  testId: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  type?: string;
}

function TextField({ label, name, required, error, testId, value, onChange, type = "text" }: FieldProps) {
  return (
    <div>
      <label htmlFor={name} className="eyebrow block mb-2">
        {label}{required && <span aria-hidden className="text-clay ml-1">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className="w-full bg-transparent border-b border-slate2/40 py-2.5 text-ink focus:border-ink outline-none transition-colors"
        data-testid={testId}
      />
      {error && (
        <p id={`${name}-error`} className="mt-2 text-sm text-clay-600 flex items-center gap-1">
          <AlertCircle size={12} aria-hidden /> {error}
        </p>
      )}
    </div>
  );
}

function TextAreaField({ label, name, required, error, testId, value, onChange, rows = 5 }: FieldProps & { rows?: number }) {
  return (
    <div>
      <label htmlFor={name} className="eyebrow block mb-2">
        {label}{required && <span aria-hidden className="text-clay ml-1">*</span>}
      </label>
      <textarea
        id={name}
        name={name}
        rows={rows}
        required={required}
        value={value}
        onChange={onChange}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className="w-full bg-transparent border-b border-slate2/40 py-2.5 text-ink focus:border-ink outline-none transition-colors resize-none"
        data-testid={testId}
      />
      {error && (
        <p id={`${name}-error`} className="mt-2 text-sm text-clay-600 flex items-center gap-1">
          <AlertCircle size={12} aria-hidden /> {error}
        </p>
      )}
    </div>
  );
}

function SelectField({
  label,
  name,
  testId,
  value,
  onChange,
  options,
}: FieldProps & { options: Array<{ id: string; label: string }> }) {
  return (
    <div>
      <label htmlFor={name} className="eyebrow block mb-2">{label}</label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-slate2/40 py-2.5 text-ink focus:border-ink outline-none transition-colors appearance-none"
        data-testid={testId}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
